import { styled } from 'styled-components'
import { ArrowDropDownIcon, Box, Button, Text, useModal, Flex, BoxProps, IconButton, ChevronDownIcon } from '@pancakeswap/uikit'
import CurrencySearchModal, { CurrencySearchModalProps } from 'components/SearchModal/CurrencySearchModal'
import EBoxSm from 'components/EBoxSm'
import { useTranslation } from '@pancakeswap/localization'
import { formatNumber } from '@pancakeswap/utils/formatBalance'
import { formatAmount } from '@pancakeswap/utils/formatFractions'
import { useCurrencyBalance } from 'state/wallet/hooks'
import { useStablecoinPrice } from 'hooks/useBUSDPrice'
import { useAccount } from 'wagmi'
import { CurrencyLogo } from '../Logo'
import { RowBetween, AutoRow } from '../Layout/Row'

const DropDownHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const DropDownContainer = styled(Button)`
  cursor: pointer;
  width: 100%;
  position: relative;
  background: #0000004D;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  padding: 16px 20px;
  user-select: none;
  z-index: 20;
  height: auto;
  justify-content: space-between;
  transition: border-color 200ms ease;

  &:hover {
    border-color: rgba(139, 92, 246, 0.4);
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    min-width: 168px;
  }

  .down-icon {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
  }
`

interface CurrencySelectProps extends CurrencySearchModalProps, BoxProps {
  label?: string
  hideBalance?: boolean
}

export const CurrencySelect = ({
  label,
  onCurrencySelect,
  selectedCurrency,
  otherSelectedCurrency,
  showCommonBases,
  commonBasesType,
  hideBalance,
  showOnlyStableCoins,
  onDismiss,
  ...props
}: CurrencySelectProps) => {
  const { address: account } = useAccount()

  const selectedCurrencyBalance = useCurrencyBalance(
    account ?? undefined,
    !hideBalance && selectedCurrency ? selectedCurrency : undefined,
  )

  const { t } = useTranslation()

  const [onPresentCurrencyModal] = useModal(
    <CurrencySearchModal
      onCurrencySelect={onCurrencySelect}
      selectedCurrency={selectedCurrency}
      otherSelectedCurrency={otherSelectedCurrency}
      showCommonBases={showCommonBases}
      commonBasesType={commonBasesType}
      showOnlyStableCoins={showOnlyStableCoins}
    />,
  )

  const price = useStablecoinPrice(selectedCurrencyBalance && selectedCurrency ? selectedCurrency : undefined)
  const quoted = selectedCurrencyBalance && price?.quote(selectedCurrencyBalance)

  return (
    <div width="100%" {...props}>
      <DropDownContainer p={0} onClick={() => {
        onDismiss?.()
        onPresentCurrencyModal()
      }}>
        <DropDownHeader>
          <Text color="white" fontSize="12px" lineHeight={1} mb={1}>{label}</Text>
          {
            !selectedCurrency ? (
              <Text color={!selectedCurrency ? 'textSubtle' : undefined} lineHeight="30px">
                {t('Select')}
              </Text>
            ) : (
              <Flex alignItems="center" justifyContent="space-between">
                <CurrencyLogo currency={selectedCurrency} size="24px" style={{ marginRight: '8px' }} />
                <Text id="pair">
                  {selectedCurrency && selectedCurrency.symbol && selectedCurrency.symbol.length > 20
                    ? `${selectedCurrency.symbol.slice(0, 4)}...${selectedCurrency.symbol.slice(-5)}`
                    : selectedCurrency?.symbol}
                </Text>
              </Flex>
            )}
        </DropDownHeader>
        <IconButton variant="tertiary" scale="sm">
          <ChevronDownIcon />
        </IconButton>
      </DropDownContainer>
      {account && !!selectedCurrency && !hideBalance && (
        <Box>
          <AutoRow justify="space-between" gap="2px">
            <Text color="textSubtle" fontSize="12px">
              {t('Balance')}:
            </Text>
            <Text fontSize="12px">{formatAmount(selectedCurrencyBalance, 6) ?? t('Loading')}</Text>
          </AutoRow>
          <RowBetween>
            <div />
            {Number.isFinite(+quoted?.toExact()) && (
              <Text fontSize="12px" color="textSubtle">
                ~${formatNumber(+quoted.toExact())}
              </Text>
            )}
          </RowBetween>
        </Box>
      )}
    </div>
  )
}
